import { User, Lead, PortfolioItem, Offer, Review, AppNotification, Transaction, Category, UserRole, AccountStatus, OfferStatus, Alarm, Conversation, Message, CreditPackage, LiveDashboardData } from '../types';

// API BASE URL - Web için relative path
const API_URL = '/api';

// --- IMAGE URL HELPER ---
/**
 * Veritabanından gelen /uploads/ veya URL formatındaki yolları 
 * tarayıcıda görüntülenebilir tam URL'ye dönüştürür.
 */
export const getImageUrl = (path: string | undefined | null): string => {
    if (!path) return '';
    if (path.startsWith('http') || path.startsWith('data:')) return path;

    // Yolun başındaki slash'ı kontrol et
    const safePath = path.startsWith('/') ? path : `/${path}`;

    // Frontend origin'i üzerinden eriş (Vite proxy veya prod servis yolu)
    return `${window.location.origin}${safePath}`;
};

// --- COOKIE HELPERS (iOS ROOT SOLUTION) ---
// iOS Safari LocalStorage'ı silebilir veya engelleyebilir, ancak Cookie'ler genellikle kalıcıdır.
const setCookie = (name: string, value: string, days: number) => {
    try {
        let expires = "";
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            expires = "; expires=" + date.toUTCString();
        }
        // SameSite=Lax ve Secure, modern tarayıcılar ve iOS için önemlidir.
        document.cookie = name + "=" + (encodeURIComponent(value) || "") + expires + "; path=/; SameSite=Lax";
    } catch (e) {
        console.error("Cookie write failed", e);
    }
};

const getCookie = (name: string): string | null => {
    try {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length));
        }
        return null;
    } catch (e) {
        return null;
    }
};

const eraseCookie = (name: string) => {
    try {
        document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    } catch (e) { }
};

// --- MULTI-LAYER STORAGE ENGINE ---
// 1. Memory (Hızlı, sayfa yenilenince gider)
// 2. LocalStorage (Standart)
// 3. SessionStorage (Yedek)
// 4. Cookies (iOS/Safari için en güvenli liman)

let memoryUser: User | null = null;
let memoryTokens: { accessToken: string; refreshToken: string; expiresIn: number } | null = null;

// JWT Token Storage Functions
const getStoredTokens = (): { accessToken: string; refreshToken: string; expiresIn: number } | null => {
    if (memoryTokens) return memoryTokens;

    let tokenStr = null;
    try { tokenStr = localStorage.getItem('talepia_jwt_tokens'); } catch (e) { }
    if (!tokenStr) try { tokenStr = sessionStorage.getItem('talepia_jwt_tokens'); } catch (e) { }
    if (!tokenStr) tokenStr = getCookie('talepia_jwt_tokens');

    if (tokenStr) {
        try {
            const parsed = JSON.parse(tokenStr);
            if (parsed && parsed.accessToken) {
                memoryTokens = parsed;
                return parsed;
            }
        } catch (e) {
            clearStoredTokens();
        }
    }
    return null;
};

const saveStoredTokens = (tokens: { accessToken: string; refreshToken: string; expiresIn: number }) => {
    memoryTokens = tokens;
    const data = JSON.stringify(tokens);
    try { localStorage.setItem('talepia_jwt_tokens', data); } catch (e) { }
    try { sessionStorage.setItem('talepia_jwt_tokens', data); } catch (e) { }
    setCookie('talepia_jwt_tokens', data, 30);
};

const clearStoredTokens = () => {
    memoryTokens = null;
    try { localStorage.removeItem('talepia_jwt_tokens'); } catch (e) { }
    try { sessionStorage.removeItem('talepia_jwt_tokens'); } catch (e) { }
    eraseCookie('talepia_jwt_tokens');
};

const getStoredUser = (): User | null => {
    // 1. Bellek Kontrolü
    if (memoryUser) return memoryUser;

    let userStr = null;

    // 2. LocalStorage Kontrolü
    try {
        userStr = localStorage.getItem('talepia_current_user');
    } catch (e) { }

    // 3. SessionStorage Kontrolü
    if (!userStr) {
        try {
            userStr = sessionStorage.getItem('talepia_current_user');
        } catch (e) { }
    }

    // 4. Cookie Kontrolü (Son Çare - iOS Restart/Refresh için)
    if (!userStr) {
        userStr = getCookie('talepia_current_user');
    }

    // Veriyi Parse Et ve Belleği Güncelle
    if (userStr) {
        try {
            // Corrupt veriyi tespit et ve temizle
            const parsed = JSON.parse(userStr);
            // Parsed verinin geçerli bir user objesi olduğunu doğrula
            if (parsed && typeof parsed === 'object' && parsed.id) {
                memoryUser = parsed;
                return parsed;
            } else {
                // Geçersiz user objesi - temizle
                clearStoredUser();
                return null;
            }
        } catch (e) {
            // Parse hatası - corrupt veriyi temizle
            console.warn('⚠️ Corrupt user data detected, clearing storage...', e);
            clearStoredUser();
            return null;
        }
    }
    return null;
};

const saveStoredUser = (user: User & { accessToken?: string; refreshToken?: string; expiresIn?: number }) => {
    // Extract and save tokens if present
    if (user.accessToken && user.refreshToken) {
        saveStoredTokens({
            accessToken: user.accessToken,
            refreshToken: user.refreshToken,
            expiresIn: user.expiresIn || 604800
        });
    }

    // Remove token fields from user object before storing
    const { accessToken, refreshToken, expiresIn, ...cleanUser } = user;

    // 1. Bellek
    memoryUser = cleanUser as User;
    const data = JSON.stringify(cleanUser);

    // 2. LocalStorage
    try {
        localStorage.setItem('talepia_current_user', data);
    } catch (e) { }

    // 3. SessionStorage
    try {
        sessionStorage.setItem('talepia_current_user', data);
    } catch (e) { }

    // 4. Cookie (30 Günlük) - En garanti yöntem
    setCookie('talepia_current_user', data, 30);
};

const clearStoredUser = () => {
    memoryUser = null;
    clearStoredTokens(); // Also clear tokens
    try { localStorage.removeItem('talepia_current_user'); } catch (e) { }
    try { sessionStorage.removeItem('talepia_current_user'); } catch (e) { }
    eraseCookie('talepia_current_user');
};

// Token refresh function
const refreshAccessToken = async (): Promise<boolean> => {
    const tokens = getStoredTokens();
    if (!tokens?.refreshToken) return false;

    try {
        const response = await fetch(`${API_URL}/auth/refresh-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: tokens.refreshToken })
        });

        if (response.ok) {
            const newTokens = await response.json();
            saveStoredTokens(newTokens);
            return true;
        }
    } catch (e) {
        console.error('Token refresh failed:', e);
    }

    // Refresh failed - clear everything
    clearStoredUser();
    return false;
};

// Helper for API calls - NOW WITH JWT SUPPORT
async function fetchAPI<T>(endpoint: string, options?: RequestInit, isRetry = false): Promise<T> {
    const user = getStoredUser();
    const tokens = getStoredTokens();

    const headers: any = { 'Content-Type': 'application/json' };

    // SECURITY: Use JWT Authorization header if available
    if (tokens?.accessToken) {
        headers['Authorization'] = `Bearer ${tokens.accessToken}`;
    }

    // Fallback to x-user-id for backward compatibility (will be deprecated)
    if (user && user.id && !tokens?.accessToken) {
        headers['x-user-id'] = user.id;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers: { ...headers, ...options?.headers }
    });

    if (response.status === 401) {
        // Try to refresh token (only once)
        if (!isRetry && tokens?.refreshToken) {
            const refreshed = await refreshAccessToken();
            if (refreshed) {
                // Retry the request with new token
                return fetchAPI<T>(endpoint, options, true);
            }
        }

        // Oturum gerçekten geçersizse temizle
        clearStoredUser();

        if (window.location.pathname !== '/' && !window.location.pathname.includes('landing')) {
            window.location.href = '/';
        }
        throw new Error('Oturum süresi doldu veya geçersiz.');
    }

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Bir hata oluştu');
    return data;
}

export const badWords = [
    'aptal', 'salak', 'gerizekalı', 'gerizekali', 'geri zekalı', 'yavşak', 'yavsak', 'adi', 'şerefsiz', 'serefsiz', 'dolandırıcı', 'sahtekar', 'üçkağıtçı', 'lan', 'mk', 'aq', 'amk', 'piç', 'gavat',
    'it', 'köpek', 'ahlaksız', 'namussuz', 'terbiyesiz', 'hakaret', 'küfür', 'şiddet', 'öl', 'öldür', 'geber', 'kan', 'bıçak', 'silah', 'adi firma'
];

export const profanityFilter = (text: string): boolean => {
    if (!text) return false;
    const lowerText = text.toLowerCase();
    return badWords.some(word => lowerText.includes(word));
};

// --- DB INTERFACE (CONNECTED TO BACKEND) ---
export const api = {

    session: {
        get: getStoredUser,
        save: saveStoredUser,
        clear: clearStoredUser
    },

    public: {
        getStats: async (): Promise<any> => fetchAPI('/stats/public')
    },

    auth: {
        sendOTP: async (phone: string): Promise<void> => {
            await fetchAPI('/auth/send-otp', { method: 'POST', body: JSON.stringify({ phone }) });
        },

        verifyOTP: async (phone: string, code: string): Promise<void> => {
            await fetchAPI('/auth/verify-otp', { method: 'POST', body: JSON.stringify({ phone, code }) });
        },

        loginCustomer: async (phone: string, fullName?: string, email?: string): Promise<User> => {
            return fetchAPI('/auth/customer-login', { method: 'POST', body: JSON.stringify({ phone, fullName, email }) });
        },

        loginProvider: async (email: string, pass: string): Promise<User> => {
            return fetchAPI('/auth/login', { method: 'POST', body: JSON.stringify({ email, password: pass }) });
        },

        register: async (userData: Partial<User>): Promise<User> => {
            return fetchAPI('/auth/register', { method: 'POST', body: JSON.stringify(userData) });
        },

        forgotPassword: async (phone: string): Promise<void> => {
            await fetchAPI('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ phone }) });
        },

        resetPassword: async (phone: string, code: string, newPassword: string): Promise<void> => {
            await fetchAPI('/auth/reset-password', { method: 'POST', body: JSON.stringify({ phone, code, newPassword }) });
        }
    },

    transactions: {
        get: async (userId: string): Promise<Transaction[]> => fetchAPI(`/transactions/${userId}`),

        // Updated to return the token data instead of User directly for PayTR init
        buyCredits: async (userId: string, amount: number, credits: number, description: string, paymentDetails?: any): Promise<any> => {
            if (paymentDetails) {
                // Using /payment/init for PayTR token generation
                return fetchAPI('/payment/init', {
                    method: 'POST',
                    body: JSON.stringify({
                        price: amount,
                        credits,
                        planName: description,
                        ...paymentDetails
                    })
                });
            }
            throw new Error("Ödeme detayları eksik.");
        },

        // New method for manual mock completion
        completeMock: async (merchant_oid: string): Promise<void> => {
            await fetchAPI('/payment/mock-complete', {
                method: 'POST',
                body: JSON.stringify({ merchant_oid })
            });
        }
    },

    users: {
        get: async (id: string): Promise<User> => fetchAPI(`/users/${id}`),
        update: async (id: string, data: Partial<User>): Promise<User> => fetchAPI(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
        getAll: async (): Promise<User[]> => fetchAPI('/users'),
        changePassword: async (id: string, oldPass: string, newPass: string) => fetchAPI(`/users/${id}/password`, { method: 'PUT', body: JSON.stringify({ oldPassword: oldPass, newPassword: newPass }) }),
        delete: async (id: string) => fetchAPI(`/users/${id}`, { method: 'DELETE' })
    },

    leads: {
        getAll: async (): Promise<Lead[]> => fetchAPI('/leads'),
        create: async (lead: Lead): Promise<Lead> => fetchAPI('/leads', { method: 'POST', body: JSON.stringify(lead) }),
        delete: async (id: string) => fetchAPI(`/leads/${id}`, { method: 'DELETE' }),
        update: async (id: string, data: Partial<Lead>) => fetchAPI(`/leads/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
        unlock: async (leadId: string, providerId: string, cost: number): Promise<User> => fetchAPI(`/leads/${leadId}/unlock`, { method: 'POST', body: JSON.stringify({ providerId, cost }) }),
        close: async (id: string) => fetchAPI(`/leads/${id}/close`, { method: 'PUT' }), // NEW: Close Lead
        getRecommendedProviders: async (leadId: string, limit?: number): Promise<any[]> => fetchAPI(`/leads/${leadId}/recommended-providers${limit ? `?limit=${limit}` : ''}`)
    },

    offers: {
        getAll: async (): Promise<Offer[]> => fetchAPI('/offers'),
        create: async (offer: Offer): Promise<Offer> => fetchAPI('/offers', { method: 'POST', body: JSON.stringify(offer) }),
        update: async (id: string, data: Partial<Offer>) => fetchAPI(`/offers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
        respond: async (id: string, status: 'ACCEPTED' | 'REJECTED' | 'REVISION'): Promise<Offer> =>
            fetchAPI(`/offers/${id}/respond`, { method: 'POST', body: JSON.stringify({ status }) })
    },

    portfolio: {
        getAll: async (providerId: string): Promise<PortfolioItem[]> => fetchAPI(`/portfolio?providerId=${providerId}`),
        add: async (item: PortfolioItem) => fetchAPI('/portfolio', { method: 'POST', body: JSON.stringify(item) }),
        create: async (item: PortfolioItem) => fetchAPI('/portfolio', { method: 'POST', body: JSON.stringify(item) }),
        update: async (id: string, item: Partial<PortfolioItem>) => fetchAPI(`/portfolio/${id}`, { method: 'PUT', body: JSON.stringify(item) }),
        delete: async (id: string) => fetchAPI(`/portfolio/${id}`, { method: 'DELETE' })
    },

    admin: {
        getStats: async () => fetchAPI<any>('/admin/stats'),
        getLiveStats: async () => fetchAPI<LiveDashboardData>('/admin/live-stats'),
        getTopProviders: async (limit?: number) => fetchAPI<User[]>(`/admin/top-providers${limit ? `?limit=${limit}` : ''}`),
        getAllTransactions: async () => fetchAPI<Transaction[]>('/admin/transactions'),
        getAnalytics: async (period?: number) => fetchAPI<any>(`/admin/analytics${period ? `?period=${period}` : ''}`),
        getContactMessages: async () => fetchAPI<AppNotification[]>('/admin/contacts'),
        giftCredits: async (userId: string, credits: number) => fetchAPI('/admin/gift-credits', { method: 'POST', body: JSON.stringify({ userId, credits }) }),
        resetSystem: async () => fetchAPI('/admin/reset-system', { method: 'POST' }),
        backfillCorporateIds: async (): Promise<{ updated: number }> => fetchAPI('/admin/backfill-corporate-ids', { method: 'POST' }),
        sendMessage: async (data: { targetType: 'SINGLE' | 'ALL_PROVIDERS' | 'ALL_CUSTOMERS' | 'ALL_BROKERS' | 'ALL', userId?: string, message: string, sendSms: boolean }) =>
            fetchAPI('/admin/message', { method: 'POST', body: JSON.stringify(data) })
    },

    settings: {
        getPackages: async () => fetchAPI<CreditPackage[]>('/settings/packages'),
        updatePackages: async (pkgs: any[]) => fetchAPI('/settings/packages', { method: 'PUT', body: JSON.stringify(pkgs) })
    },

    notifications: {
        getAll: async (userId: string): Promise<AppNotification[]> => fetchAPI(`/notifications?userId=${userId}`),
        create: async (notif: Partial<AppNotification>) => fetchAPI('/notifications', { method: 'POST', body: JSON.stringify(notif) }),
        markAsRead: async (id: string) => fetchAPI(`/notifications/${id}/read`, { method: 'PUT' }),
        markAllAsRead: async (userId: string) => fetchAPI('/notifications/read-all', { method: 'PUT', body: JSON.stringify({ userId }) }),
        delete: async (id: string) => fetchAPI(`/notifications/${id}`, { method: 'DELETE' }),
        // Helper to trigger SMS via admin endpoint (since direct SMS API isn't public, reusing admin channel for notifications)
        sendSMS: async (userId: string, message: string) => fetchAPI('/admin/message', {
            method: 'POST',
            body: JSON.stringify({ targetType: 'SINGLE', userId, message, sendSms: true })
        })
    },

    reviews: {
        getAll: async (): Promise<Review[]> => fetchAPI('/reviews'),
        add: async (review: Review) => fetchAPI('/reviews', { method: 'POST', body: JSON.stringify(review) }),
        create: async (review: Review) => fetchAPI('/reviews', { method: 'POST', body: JSON.stringify(review) }),
        reply: async (reviewId: string, reply: string) => fetchAPI(`/reviews/${reviewId}/reply`, { method: 'POST', body: JSON.stringify({ reply }) })
    },

    alarms: {
        getAll: async (providerId: string): Promise<Alarm[]> => fetchAPI(`/alarms/${providerId}`),
        add: async (alarm: Alarm) => fetchAPI('/alarms', { method: 'POST', body: JSON.stringify(alarm) }),
        create: async (alarm: Alarm) => fetchAPI('/alarms', { method: 'POST', body: JSON.stringify(alarm) }),
        delete: async (id: string) => fetchAPI(`/alarms/${id}`, { method: 'DELETE' })
    },

    contact: {
        send: async (form: any) => fetchAPI('/contact', { method: 'POST', body: JSON.stringify(form) })
    },

    messages: {
        getConversations: async (userId: string): Promise<Conversation[]> => fetchAPI(`/conversations/${userId}`),
        getHistory: async (userId: string, otherId: string): Promise<Message[]> => fetchAPI(`/messages/${userId}/${otherId}`),
        send: async (senderId: string, receiverId: string, content: string): Promise<Message> =>
            fetchAPI('/messages', { method: 'POST', body: JSON.stringify({ senderId, receiverId, content }) })
    },

    market: {
        analyze: async (data: { category: string, listingType: string, budgetMax: string, city: string }) => fetchAPI<{ avgPrice: number, diffPercent: number, status: string, recommendation: string }>('/market/analyze', { method: 'POST', body: JSON.stringify(data) })
    },

    // KVKK İzin Kayıtları
    consent: {
        // Tekli izin kaydı
        log: async (data: {
            phone: string,
            email?: string,
            userId?: string,
            consentType: string,
            consentVersion: string,
            consentGiven?: boolean,
            consentText?: string
        }) => fetchAPI('/consent', { method: 'POST', body: JSON.stringify(data) }),

        // Toplu izin kaydı (talep oluşturma sırasında)
        logBulk: async (data: {
            phone: string,
            email?: string,
            userId?: string,
            consents: Array<{
                consentType: string,
                consentVersion: string,
                consentGiven?: boolean,
                consentText?: string
            }>
        }) => fetchAPI('/consent/bulk', { method: 'POST', body: JSON.stringify(data) }),

        // Kullanıcının izin geçmişini getir
        getByPhone: async (phone: string) => fetchAPI(`/consent/${encodeURIComponent(phone)}`)
    },

    // Broker Sistemi
    broker: {
        // Danışmana gelen davetleri getir
        getReceivedInvitations: async (): Promise<any[]> => fetchAPI('/broker/invitations?type=received'),

        // Broker'ın gönderdiği davetleri getir
        getSentInvitations: async (): Promise<any[]> => fetchAPI('/broker/invitations?type=sent'),

        // Davete yanıt ver (kabul/red)
        respondToInvitation: async (invitationId: string, action: 'accept' | 'reject'): Promise<any> =>
            fetchAPI(`/broker/invitation/${invitationId}/respond`, {
                method: 'POST',
                body: JSON.stringify({ action })
            }),

        // Danışman ara (kurumsal ID ile)
        searchConsultant: async (corporateId: string): Promise<any> =>
            fetchAPI(`/broker/search-consultant?corporateId=${corporateId}`),

        // Danışmana davet gönder
        inviteConsultant: async (consultantId: string, branchName: string): Promise<any> =>
            fetchAPI('/broker/invite', {
                method: 'POST',
                body: JSON.stringify({ consultantId, branchName })
            }),

        // Danışmanları getir
        getConsultants: async (): Promise<any[]> => fetchAPI('/broker/consultants'),

        // Danışmanı ekipten çıkar
        removeConsultant: async (consultantId: string): Promise<any> =>
            fetchAPI(`/broker/consultant/${consultantId}`, { method: 'DELETE' }),

        // Performans raporu
        getPerformance: async (period?: '7' | '30' | 'all'): Promise<any> =>
            fetchAPI(`/broker/performance${period ? `?period=${period}` : ''}`)
    },

    // External Portfolio API Integration
    externalPortfolio: {
        getConfigs: async (): Promise<any[]> => fetchAPI('/external-portfolio/configs'),
        getConfig: async (id: string): Promise<any> => fetchAPI(`/external-portfolio/configs/${id}`),
        createConfig: async (data: any): Promise<any> => fetchAPI('/external-portfolio/configs', { method: 'POST', body: JSON.stringify(data) }),
        updateConfig: async (id: string, data: any): Promise<any> => fetchAPI(`/external-portfolio/configs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
        deleteConfig: async (id: string): Promise<any> => fetchAPI(`/external-portfolio/configs/${id}`, { method: 'DELETE' }),
        testConnection: async (id: string): Promise<any> => fetchAPI(`/external-portfolio/configs/${id}/test`, { method: 'POST' }),
        triggerSync: async (id: string): Promise<any> => fetchAPI(`/external-portfolio/configs/${id}/sync`, { method: 'POST' }),
        getSyncHistory: async (configId: string): Promise<any[]> => fetchAPI(`/external-portfolio/syncs/${configId}`),
        // Admin endpoints
        adminGetAllConfigs: async (): Promise<any[]> => fetchAPI('/external-portfolio/admin/configs'),
        adminGetStats: async (): Promise<any> => fetchAPI('/external-portfolio/admin/stats')
    }
};